import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  verifyEmail,
  resendVerification,
  forgotPassword,
  resetPassword,
} from '../../api/auth';
import http from '../../api/http';
import './Auth.css';

const RESEND_COOLDOWN_SEC = 60;

function resendStorageKey(email) {
  return `lavsan-verify-resend:${String(email || '').trim().toLowerCase()}`;
}

function readResendLeft(email) {
  const until = Number(sessionStorage.getItem(resendStorageKey(email)) || 0);
  return Math.max(0, Math.ceil((until - Date.now()) / 1000));
}

function armResendCooldown(email, sec = RESEND_COOLDOWN_SEC) {
  sessionStorage.setItem(resendStorageKey(email), String(Date.now() + sec * 1000));
}

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login: ctxLogin, register: ctxRegister, refreshMe } = useAuth();

  const [agreePD, setAgreePD] = useState(false);
  const [agreeOferta, setAgreeOferta] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [mode, setMode] = useState('login'); // login | register | check-email | forgot | reset | verify
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    newPassword: '',
    token: '',
  });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendLeft, setResendLeft] = useState(0);

  useEffect(() => {
    if (user) navigate('/account', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (mode !== 'check-email') return undefined;
    setResendLeft(readResendLeft(form.email));
    const id = setInterval(() => setResendLeft(readResendLeft(form.email)), 1000);
    return () => clearInterval(id);
  }, [mode, form.email]);

  // Ссылки из писем: /auth/verify?token=&email=  /auth/reset?token=&email=
  useEffect(() => {
    const path = window.location.pathname;
    const token = searchParams.get('token') || '';
    const email = searchParams.get('email') || '';
    if (path.includes('/auth/verify') && token) {
      setMode('verify');
      setForm((p) => ({ ...p, token, email }));
      (async () => {
        setBusy(true);
        setError('');
        try {
          await verifyEmail(token, email);
          await refreshMe();
        } catch (err) {
          setError(err?.response?.data?.error || 'Не удалось подтвердить email');
        } finally {
          setBusy(false);
        }
      })();
    } else if (path.includes('/auth/reset') && token) {
      setMode('reset');
      setForm((p) => ({ ...p, token, email }));
    }
  }, [searchParams, refreshMe]);

  const setField = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
  };

  const toRegister = () => {
    setError('');
    setInfo('');
    setAgreePD(false);
    setAgreeOferta(false);
    setAgreeMarketing(false);
    setMode('register');
  };

  const toLogin = () => {
    setError('');
    setInfo('');
    setMode('login');
  };

  const toForgot = () => {
    setError('');
    setInfo('');
    setMode('forgot');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setBusy(true);
    try {
      const loginId = form.email.trim();
      if (loginId.includes('@')) {
        await ctxLogin(loginId, form.password);
      } else {
        // fallback для старых аккаунтов по телефону
        await http.post('/api/auth/login', { phone: loginId, password: form.password });
        await refreshMe();
      }
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === 'EMAIL_NOT_VERIFIED') {
        setMode('check-email');
        setError('Подтвердите email — проверьте почту');
        return;
      }
      setError(err?.response?.data?.error || err?.message || 'Ошибка входа');
    } finally {
      setBusy(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!agreePD) {
      setError('Нужно согласие на обработку персональных данных');
      return;
    }
    if (!agreeOferta) {
      setError('Нужно принять условия публичной оферты');
      return;
    }
    setBusy(true);
    try {
      await ctxRegister({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        agreePersonalData: true,
        agreeOferta: true,
        agreeMarketing,
      });
      armResendCooldown(form.email);
      setMode('check-email');
      setInfo('Мы отправили ссылку для подтверждения на ваш email');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Ошибка регистрации');
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await forgotPassword(form.email.trim());
      setInfo('Если email найден — отправили ссылку для сброса пароля');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Не удалось отправить письмо');
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await resetPassword({
        email: form.email.trim(),
        token: form.token,
        newPassword: form.newPassword,
      });
      await refreshMe();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Не удалось сменить пароль');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-box">
      {mode === 'login' && (
        <>
          <h2>Вход</h2>
          <form onSubmit={handleLogin}>
            <input
              type="text"
              value={form.email}
              onChange={setField('email')}
              placeholder="Email"
              autoComplete="username"
              required
            />
            <input
              type="password"
              value={form.password}
              onChange={setField('password')}
              placeholder="Пароль"
              autoComplete="current-password"
              required
            />
            <button type="submit" className="auth-btn" disabled={busy}>
              {busy ? 'Вхожу…' : 'Войти'}
            </button>
          </form>
          {error && <div className="error-msg">{error}</div>}
          <div className="auth-actions">
            <button type="button" className="auth-btn auth-btn--ghost" onClick={toRegister}>
              Регистрация
            </button>
            <button type="button" className="auth-btn auth-btn--ghost" onClick={toForgot}>
              Забыл пароль
            </button>
          </div>
        </>
      )}

      {mode === 'register' && (
        <>
          <h2>Регистрация</h2>
          <form onSubmit={handleRegister}>
            <input value={form.name} onChange={setField('name')} placeholder="Имя" autoComplete="name" required />
            <input
              type="email"
              value={form.email}
              onChange={setField('email')}
              placeholder="Email"
              autoComplete="email"
              required
            />
            <input
              type="password"
              value={form.password}
              onChange={setField('password')}
              placeholder="Пароль (от 8 символов)"
              autoComplete="new-password"
              required
              minLength={8}
            />
            <label className="pd-label">
              <input
                type="checkbox"
                checked={agreePD}
                onChange={(e) => setAgreePD(e.target.checked)}
                required
              />
              <span>
                Я даю согласие на обработку персональных данных в соответствии с{' '}
                <Link to="/privacy" target="_blank" rel="noreferrer">Политикой конфиденциальности</Link>
              </span>
            </label>
            <label className="pd-label">
              <input
                type="checkbox"
                checked={agreeOferta}
                onChange={(e) => setAgreeOferta(e.target.checked)}
                required
              />
              <span>
                Я принимаю условия{' '}
                <Link to="/oferta" target="_blank" rel="noreferrer">публичной оферты</Link>
              </span>
            </label>
            <label className="pd-label">
              <input
                type="checkbox"
                checked={agreeMarketing}
                onChange={(e) => setAgreeMarketing(e.target.checked)}
              />
              <span>
                Согласен получать рекламные и информационные рассылки (email, SMS, мессенджеры).
                Можно не отмечать и отозвать позже. Подробнее — в{' '}
                <Link to="/privacy#marketing" target="_blank" rel="noreferrer">политике</Link>
              </span>
            </label>
            <button type="submit" className="auth-btn" disabled={!agreePD || !agreeOferta || busy}>
              {busy ? 'Отправляю…' : 'Зарегистрироваться'}
            </button>
          </form>
          {error && <div className="error-msg">{error}</div>}
          <div className="auth-actions">
            <button type="button" className="auth-btn auth-btn--ghost" onClick={toLogin}>
              Назад ко входу
            </button>
          </div>
        </>
      )}

      {mode === 'check-email' && (
        <>
          <h2>Проверьте почту</h2>
          <p className="auth-hint">
            Мы отправили ссылку для подтверждения на <b>{form.email || 'ваш email'}</b>.
            После перехода по ссылке вход откроется автоматически.
          </p>
          {info && <div className="info-msg">{info}</div>}
          {error && <div className="error-msg">{error}</div>}
          <div className="auth-actions">
            <button
              type="button"
              className="auth-btn"
              disabled={busy || !form.email || resendLeft > 0}
              onClick={async () => {
                setBusy(true);
                setError('');
                try {
                  await resendVerification(form.email.trim());
                  armResendCooldown(form.email);
                  setResendLeft(RESEND_COOLDOWN_SEC);
                  setInfo('Письмо отправлено повторно');
                } catch (err) {
                  const sec = Number(err?.response?.data?.retryAfterSec);
                  if (err?.response?.status === 429 && sec > 0) {
                    armResendCooldown(form.email, sec);
                    setResendLeft(sec);
                    setError('');
                    return;
                  }
                  setError(err?.response?.data?.error || 'Не удалось отправить');
                } finally {
                  setBusy(false);
                }
              }}
            >
              {resendLeft > 0 ? `Отправить ещё раз через ${resendLeft} с` : 'Отправить ещё раз'}
            </button>
            <button type="button" className="auth-btn auth-btn--ghost" onClick={toLogin}>
              Ко входу
            </button>
          </div>
        </>
      )}

      {mode === 'verify' && (
        <>
          <h2>Подтверждение email</h2>
          {busy && <p>Подтверждаем…</p>}
          {error && <div className="error-msg">{error}</div>}
          {!busy && !error && <p>Готово — выполняем вход…</p>}
          <div className="auth-actions">
            <button type="button" className="auth-btn auth-btn--ghost" onClick={toLogin}>
              Ко входу
            </button>
          </div>
        </>
      )}

      {mode === 'forgot' && (
        <>
          <h2>Восстановление</h2>
          <form onSubmit={handleForgot}>
            <input
              type="email"
              value={form.email}
              onChange={setField('email')}
              placeholder="Email"
              autoComplete="email"
              required
            />
            <button type="submit" className="auth-btn" disabled={busy}>
              {busy ? 'Отправляю…' : 'Отправить ссылку'}
            </button>
          </form>
          {info && <div className="info-msg">{info}</div>}
          {error && <div className="error-msg">{error}</div>}
          <div className="auth-actions">
            <button type="button" className="auth-btn auth-btn--ghost" onClick={toLogin}>
              Назад
            </button>
          </div>
        </>
      )}

      {mode === 'reset' && (
        <>
          <h2>Новый пароль</h2>
          <form onSubmit={handleReset}>
            <input
              type="email"
              value={form.email}
              onChange={setField('email')}
              placeholder="Email"
              autoComplete="email"
              required
            />
            <input
              type="password"
              value={form.newPassword}
              onChange={setField('newPassword')}
              placeholder="Новый пароль"
              autoComplete="new-password"
              required
              minLength={8}
            />
            <button type="submit" className="auth-btn" disabled={busy}>
              {busy ? 'Сохраняю…' : 'Сменить пароль'}
            </button>
          </form>
          {error && <div className="error-msg">{error}</div>}
          <div className="auth-actions">
            <button type="button" className="auth-btn auth-btn--ghost" onClick={toLogin}>
              Ко входу
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Auth;
